import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { adminProductLineService } from '@/services/admin-product-line.service'
import type { ProductLineFilters, CreateProductLineRequest, UpdateProductLineRequest } from '@/types/product-line'

export function useProductLines(filters: ProductLineFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['admin', 'product-lines', filters],
    queryFn: () => adminProductLineService.getProductLines(filters),
    // On unless a caller says otherwise: a screen that only reads the lines inside a popover or a
    // dialog holds the request back until that opens.
    enabled: options?.enabled ?? true,
    // Keep the current rows on screen while the next page/filter loads; the list page pairs this
    // with isFetching for its "Updating…" pill instead of flashing skeleton rows.
    placeholderData: (previousData) => previousData,
  })
}

export function useProductLine(id: string) {
  return useQuery({
    queryKey: ['admin', 'product-lines', id],
    queryFn: () => adminProductLineService.getProductLineById(id),
    enabled: !!id,
  })
}

export function useCreateProductLine() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateProductLineRequest) => adminProductLineService.createProductLine(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'product-lines'] })
      toast.success('Product line created successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to create product line: ${error.message}`)
    },
  })
}

export function useUpdateProductLine() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductLineRequest }) =>
      adminProductLineService.updateProductLine(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'product-lines'] })
      toast.success('Product line updated successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to update product line: ${error.message}`)
    },
  })
}

export function useDeleteProductLine() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => adminProductLineService.deleteProductLine(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'product-lines'] })
      toast.success('Product line deleted successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete product line: ${error.message}`)
    },
  })
}
